import { useMemo, useState } from "react";
import { Button } from "@its-engineering-and-development/ui-foundation/components";
import type { SortDirection } from "@/components/base/column-filter-popover";
import { useMileagePolicyRules } from "@app/hooks/use-policy";
import type { MileagePolicyRule } from "@app/types/policy";
import { PolicyLoadingPlaceholder, PolicyPageError, PolicyTableEmptyState, PolicyTableShell } from "./policy-table-header";
import { PolicyStatusBadge } from "./policy-status-badge";
import { POLICY_STATUS_FILTER_OPTIONS } from "./policy-filter-options";
import { processPolicyRows } from "./policy-row-processing";
import { usePolicyTableColumnHeader } from "./use-policy-table-column-header";
import { usePolicyTablePagination } from "./use-policy-table-pagination";
import { formatDate, toDateTimestamp } from "@/utils/date";

const MILEAGE_SEARCH_HISTORY_KEY = "expense-admin:mileage-policy-column-search-history";

type MileageColumnKey = "ruleName" | "status" | "rate" | "unit" | "updatedBy" | "changedDate";

const EXACT_MATCH_COLUMNS = new Set<MileageColumnKey>(["status"]);

function getMileageCellValue(rule: MileagePolicyRule, column: MileageColumnKey): string {
    switch (column) {
        case "ruleName":
            return rule.ruleName;
        case "status":
            return rule.status;
        case "rate":
            return rule.rate;
        case "unit":
            return rule.unit;
        case "updatedBy":
            return rule.updatedBy;
        case "changedDate":
            return formatDate(rule.changedDate);
    }
}

function getMileageSortValue(rule: MileagePolicyRule, column: MileageColumnKey): string | number {
    switch (column) {
        case "changedDate":
            return toDateTimestamp(rule.changedDate);
        default:
            return getMileageCellValue(rule, column);
    }
}

function IconPlus() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 5v14M5 12h14" />
        </svg>
    );
}

export function MileagePolicyPage() {
    const [activeFilters, setActiveFilters] = useState<Partial<Record<MileageColumnKey, string>>>({});
    const [sort, setSort] = useState<{ by: MileageColumnKey; order: SortDirection } | null>(null);
    const { rules, isLoading, error } = useMileagePolicyRules();

    const colHeader = usePolicyTableColumnHeader(
        MILEAGE_SEARCH_HISTORY_KEY,
        activeFilters,
        sort,
        (column, value) => setActiveFilters((prev) => ({ ...prev, [column]: value })),
        (column) =>
            setActiveFilters((prev) => {
                const next = { ...prev };
                delete next[column];
                return next;
            }),
        (column) =>
            setSort((prev) => {
                if (!prev || prev.by !== column) return { by: column, order: "asc" };
                if (prev.order === "asc") return { by: column, order: "desc" };
                return null;
            }),
    );

    const filteredRules = useMemo(
        () => processPolicyRows(rules, getMileageCellValue, activeFilters, sort, EXACT_MATCH_COLUMNS, getMileageSortValue),
        [rules, activeFilters, sort],
    );

    const hasActiveFilters = Object.keys(activeFilters).length > 0;
    const paginationResetKey = useMemo(() => `${JSON.stringify(activeFilters)}|${sort?.by ?? ""}|${sort?.order ?? ""}`, [activeFilters, sort]);
    const { paginatedRows, pageInfo, setPage } = usePolicyTablePagination(filteredRules, paginationResetKey);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Mileage Policy</h1>
                    <p className="mt-1 text-sm text-gray-500">Configure mileage reimbursement rates, distance rules, and automated policy responses.</p>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                    <Button color="primary" size="md" isDisabled title="Add Mileage Policy not yet supported" iconLeading={IconPlus}>
                        Add Mileage Policy
                    </Button>
                </div>
            </div>

            {error && <PolicyPageError message={error} />}

            {isLoading && rules.length === 0 ? (
                <PolicyLoadingPlaceholder label="Loading mileage policy…" />
            ) : (
                <PolicyTableShell pageInfo={pageInfo} onPageChange={setPage} isLoading={isLoading} loadingLabel="Loading mileage policy…">
                    <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left">
                        <tr>
                            {colHeader("ruleName", "Rule name")}
                            {colHeader("status", "Status", { options: POLICY_STATUS_FILTER_OPTIONS })}
                            {colHeader("rate", "Rate")}
                            {colHeader("unit", "Unit")}
                            {colHeader("updatedBy", "Updated by")}
                            {colHeader("changedDate", "Changed date")}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {!isLoading && filteredRules.length === 0 ? (
                            <PolicyTableEmptyState colSpan={6} message={hasActiveFilters ? "No rules match the current filters." : "No rules found."} />
                        ) : (
                            paginatedRows.map((rule) => (
                                <tr key={rule.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-blue-600">{rule.ruleName}</td>
                                    <td className="px-4 py-3">
                                        <PolicyStatusBadge status={rule.status} />
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">{rule.rate}</td>
                                    <td className="px-4 py-3 text-gray-700">{rule.unit}</td>
                                    <td className="px-4 py-3 text-gray-700">{rule.updatedBy}</td>
                                    <td className="px-4 py-3 text-gray-700">{getMileageCellValue(rule, "changedDate")}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </PolicyTableShell>
            )}
        </div>
    );
}
